import { useEffect, useRef, useState } from 'react';
import './FundSummaryWidget.css';
import Layout from '../../Components/Common/Layout/Layout';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import {
  BaseWidgetProps,
  WidgetHeightMessage,
  WidgetMessage,
} from '../../types/common.type';
import {
  SaleInCountry,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import { errorMsg } from '../../constants/common.constant';
import FundSummaryHeader from './components/Header/Header';
import FundObjective from './components/FundObjective/FundObjective';
import FundPerformance from './components/FundPerformance/FundPerformance';
import FundChart from './components/FundChart/FundChart';
import LipperRating from './components/LipperRating/LipperRating';
import TopHolding from './components/TopHolding/TopHolding';

import useFundSummaryData from '../../hooks/data/useFundSummaryData';
import { FundChartPeriod } from '../../types/fundsummary.type';
import FundInfoList from './components/FundInfoList/FundInfoList';
import useContainerWidth from '../../hooks/useContainerWidth';

const DEFAULT_CHART_PERIOD: FundChartPeriod = '6M';

export interface FundSummaryWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  saleInCountryCode?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const FundSummaryWidget = ({
  token = '',
  symbol = '',
  saleInCountryCode = SaleInCountry.AU,
  theme = ThemeType.Direct,
  onLoadingChange,
  onError,
  onNoData,
  onHeightChange,
}: FundSummaryWidgetProps) => {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const { isSmallScreen } = useContainerWidth(widgetRef);
  useResizeNotifier(widgetRef, WidgetNameSource.FundSummary, onHeightChange);
  const [period, setPeriod] = useState<FundChartPeriod>(DEFAULT_CHART_PERIOD);
  const {
    info,
    holdings,
    quote,
    performance,
    chart,
    lipperRating,
    isLoading,
    isChartLoading,
    error,
  } = useFundSummaryData(symbol, token, saleInCountryCode, period);
  const msgItem = errorMsg.find((obj) => obj.key === 'fundSummary');

  useEffect(() => {
    onLoadingChange?.(isLoading || isChartLoading);
  }, [isLoading, isChartLoading, onLoadingChange]);

  useEffect(() => {
    if (isLoading || isChartLoading || !error) return;

    onError?.({
      message: error,
      type: 'AxiosError_ERR_BAD_REQUEST',
    });
  }, [isLoading, isChartLoading, error, onError]);

  useEffect(() => {
    if (isLoading || isChartLoading || error) return;

    const hasData = Boolean(
      info?.fundDetailInfo && Object.keys(info.fundDetailInfo).length > 0,
    );
    if (hasData) return;

    onNoData?.({
      type: WidgetMessageType.NoData,
      source: 'FundSummaryWidget',
      data: null,
    });
  }, [isLoading, isChartLoading, error, info, onNoData]);

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="fund-summary-container">
        {isLoading ? (
          <div className="fund-summary-state">
            <Loading />
          </div>
        ) : !isLoading && error ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="fund-summary-content">
            {info && quote && <FundSummaryHeader quote={quote} info={info} />}
            <FundObjective
              content={info?.fundDetailInfo?.objective as string}
            />
            <div className="fund-summary-row">
              <div className="fund-summary-row__col">
                <FundChart
                  theme={theme}
                  period={period}
                  data={chart}
                  isLoading={isChartLoading}
                  onPeriodChange={(next) => setPeriod(next as FundChartPeriod)}
                />
                {performance && (
                  <FundPerformance
                    data={performance}
                    updateDate={quote?.TRADE_DATE as string}
                  />
                )}
              </div>
              {!isSmallScreen && <div className="fund-summary-row__spacer" />}
              <div className="fund-summary-row__col">
                <LipperRating
                  lipperRating={lipperRating}
                  updateDate={quote?.TRADE_DATE as string}
                />
                <TopHolding holdings={holdings} />
              </div>
            </div>

            {info && (
              <div className="fund-summary-row">
                <div className="fund-summary-row__col">
                  <FundInfoList info={info} variant="facts" />
                </div>

                {!isSmallScreen && <div className="fund-summary-row__spacer" />}

                <div className="fund-summary-row__col">
                  <FundInfoList info={info} variant="profile" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FundSummaryWidget;
